// 選色 plate — the storefront PDP's all-colours flat lay, with each colour named
// in place, for warehouse staff who can't tell 灰色 from 淺灰 with the garment in
// hand.
//
// The PDP shows ONE marker at a time (the shopper already picked a colour).
// Receiving is the inverse problem — you hold the garment and need the NAME —
// so every colour is labelled directly on its garment. A numbered-dot + legend
// version was tried first and rejected: it makes the eye ping-pong between the
// plate and a key for something the label can just say.
//
// Coordinates are percentages of the LAST product image (listing convention:
// last image = all-colours flat lay), which is the image the markers were
// placed against — see backend GET /api/receiving/color-plate.
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, Box, Typography, CircularProgress, Alert, Button as MuiButton,
} from '@mui/material';
import api from '../../services/api';

export interface ColorMarker {
  color: string;
  x_pct: number;
  y_pct: number;
}

interface PlateData {
  found: boolean;
  handle?: string;
  title?: string;
  imageUrl?: string | null;
  colors: string[];
  markers: ColorMarker[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Metanoia SKU off the receiving line — the primary lookup key. */
  sku?: string;
  /** Fallback lookup when the platform dropped the SKU, and the dialog title. */
  productName?: string;
  /** Colour of the variant being received — pre-highlighted on open. */
  highlightColor?: string;
}

const INK = '#2B3A4E';
const LINE = '#e8e5e1';
const MUTED = '#8a8480';
const PAPER = '#faf9f7';

const ColorPlateDialog: React.FC<Props> = ({ open, onClose, sku, productName, highlightColor }) => {
  const [data, setData] = useState<PlateData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    if (!sku && !productName) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    const params: Record<string, string> = {};
    if (sku) params.sku = sku;
    else if (productName) params.title = productName;
    api.get('/api/receiving/color-plate', { params })
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(err => { if (!cancelled) setError(err?.message || '讀取失敗'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, sku, productName]);

  // Pre-highlight the variant's own colour when it actually has a marker.
  useEffect(() => {
    if (!open) { setSelected(''); return; }
    if (!data?.markers?.length) return;
    const want = (highlightColor || '').trim();
    setSelected(want && data.markers.some(m => m.color === want) ? want : '');
  }, [open, data, highlightColor]);

  const markers = data?.markers || [];
  const markerColors = new Set(markers.map(m => m.color));
  // Colours the product sells but nobody marked — shown so staff know the
  // absence is missing data, not a colour that doesn't exist.
  const unmarked = (data?.colors || []).filter(c => !markerColors.has(c));
  const highlightMissing = !!highlightColor && markers.length > 0 && !markerColors.has(highlightColor.trim());

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden' } }}
    >
      {/* Header — product first, SKU as a quiet mono tag */}
      <Box sx={{ px: 2.25, pt: 2, pb: 1.5 }}>
        <Typography sx={{ fontSize: '0.66rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: MUTED, fontWeight: 700 }}>
          選色對照
        </Typography>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: INK, lineHeight: 1.45, mt: 0.5 }}>
          {productName}
        </Typography>
        {sku && (
          <Box component="span" sx={{
            display: 'inline-block', mt: 0.75, px: 0.75, py: '1px',
            fontFamily: '"DM Mono", ui-monospace, monospace', fontSize: '0.7rem', color: MUTED,
            bgcolor: PAPER, border: `1px solid ${LINE}`, borderRadius: '5px',
          }}>{sku}</Box>
        )}
      </Box>

      <DialogContent sx={{ px: 2.25, pt: 0, pb: 2 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={26} /></Box>
        )}

        {!loading && error && <Alert severity="error" sx={{ borderRadius: '8px' }}>讀取失敗：{error}</Alert>}

        {!loading && !error && data && !data.found && (
          <Alert severity="info" sx={{ borderRadius: '8px' }}>
            在官網找不到這個商品{sku ? `（SKU ${sku}）` : ''}，沒有選色圖可以對照。
          </Alert>
        )}

        {!loading && !error && data?.found && !data.imageUrl && (
          <Alert severity="info" sx={{ borderRadius: '8px' }}>這個商品在官網沒有圖片。</Alert>
        )}

        {!loading && !error && data?.found && data.imageUrl && (
          <>
            <Box sx={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', bgcolor: PAPER }}>
              <Box component="img" src={data.imageUrl} alt="" sx={{ width: '100%', display: 'block' }} />
              {markers.map(m => {
                const active = selected === m.color;
                return (
                  <Box
                    key={m.color}
                    onClick={() => setSelected(active ? '' : m.color)}
                    sx={{
                      position: 'absolute',
                      left: `${m.x_pct}%`, top: `${m.y_pct}%`,
                      transform: 'translate(-50%, -50%)',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px',
                      cursor: 'pointer', zIndex: active ? 2 : 1,
                    }}
                  >
                    {/* Small ring, not a blob — it points, it doesn't cover the garment */}
                    <Box sx={{
                      width: 9, height: 9, borderRadius: '999px',
                      bgcolor: active ? INK : '#fff',
                      border: `1.5px solid ${INK}`,
                      boxShadow: active ? `0 0 0 3px rgba(43,58,78,0.16)` : '0 0 0 3px rgba(255,255,255,0.75)',
                    }} />
                    <Box sx={{
                      px: 1, py: '2.5px', borderRadius: '999px', whiteSpace: 'nowrap',
                      fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.02em',
                      bgcolor: active ? INK : 'rgba(255,255,255,0.94)',
                      color: active ? '#fff' : INK,
                      border: `1px solid ${active ? INK : 'rgba(43,58,78,0.14)'}`,
                      boxShadow: '0 1px 4px rgba(30,25,20,0.1)',
                    }}>{m.color}</Box>
                  </Box>
                );
              })}
            </Box>

            {markers.length === 0 && (
              <Alert severity="info" sx={{ mt: 1.25, borderRadius: '8px', fontSize: '0.8rem' }}>
                這個商品還沒標記選色位置，上面是官網的全色平拍圖，請自行對照。
              </Alert>
            )}

            {markers.length > 0 && (
              <Typography sx={{ mt: 1.25, fontSize: '0.72rem', color: MUTED }}>
                {highlightColor && !highlightMissing && (
                  <>這筆是 <Box component="b" sx={{ color: INK, fontWeight: 700 }}>{highlightColor}</Box>　·　</>
                )}
                點名字可放大確認
              </Typography>
            )}

            {highlightMissing && (
              <Typography sx={{ mt: 0.5, fontSize: '0.72rem', color: '#b45309', fontWeight: 600 }}>
                這筆的「{highlightColor}」沒有標記位置
              </Typography>
            )}

            {unmarked.length > 0 && (
              <Typography sx={{ mt: 0.5, fontSize: '0.72rem', color: MUTED }}>
                未標記位置：{unmarked.join('、')}
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25,
        px: 2.25, py: 1.25, borderTop: `1px solid ${LINE}`, bgcolor: PAPER,
      }}>
        {data?.handle ? (
          <Box component="a" href={`https://metanoia.tw/products/${data.handle}`} target="_blank" rel="noreferrer"
            sx={{ fontSize: '0.72rem', color: MUTED, textDecoration: 'none', borderBottom: `1px solid ${LINE}` }}>
            在官網開啟商品頁 ↗
          </Box>
        ) : <span />}
        <MuiButton onClick={onClose} sx={{
          textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', color: INK,
          bgcolor: '#fff', border: `1px solid ${LINE}`, borderRadius: '9px', px: 2, py: 0.75,
          '&:hover': { bgcolor: PAPER },
        }}>關閉</MuiButton>
      </Box>
    </Dialog>
  );
};

export default ColorPlateDialog;
